/**
 * Host Me - Standard Date Formatting Utilities
 *
 * All dates should use Arabic locale with Gregorian calendar
 * for consistency across the application.
 *
 * Usage:
 *   import { formatDate, formatDateTime } from '../utils/dateFormatter';
 *   formatDate('2025-01-15')  // "15 يناير 2025"
 *
 * Each function below carries an `@category` tag saying whether the business
 * timezone applies to it. Set it correctly on anything new — it is the whole
 * basis for the Riyadh display convergence (tasks/force-riyadh-timezone.md):
 *
 *   instant   — a real moment; render in BUSINESS_TIMEZONE (backend Category A)
 *   date-only — a calendar day; must not shift (backend Category B)
 *   wall-clock— a rule like "16:00", built from parts; NEVER timezone-convert
 *
 * i18n: `formatDate` and `formatDateTime` take a `language` param ('ar' default).
 * The rest are Arabic-only — extend them as more pages are translated, per
 * docs/03_Technical/I18N_STRATEGY.md. Date VALUES (input defaults, query params)
 * belong in `dateValueHelper.js`, not here.
 *
 * @see docs/02_Product/design_system/HOST_ME_UI_DESIGN_SYSTEM.md — locale mandate
 * @see docs/03_Technical/TIMEZONE_IMPLEMENTATION_STATUS.md — Category A/B, the rules of record
 */

import { BUSINESS_TIMEZONE } from '../config/timezoneConfig';

/**
 * Riyadh calendar day (YYYY-MM-DD) for an instant — used to compare two moments
 * by business day. `Date.toDateString()` cannot do this: it always answers in the
 * browser's zone, which is what made two dashboards disagree on 2026-07-21.
 * @param {Date} d
 * @returns {string} YYYY-MM-DD as seen in BUSINESS_TIMEZONE
 */
const businessDayKey = (d) =>
  d.toLocaleDateString('en-CA', { timeZone: BUSINESS_TIMEZONE });

/**
 * Format date with Gregorian calendar
 * @category instant | date-only — business timezone applies to both
 * @param {string|Date} dateString - Date to format
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @param {string} language - Locale language: 'ar' (default) or 'en'
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}, language = 'ar') => {
  if (!dateString) return language === 'en' ? 'Not set' : 'غير محدد';

  const locale = language === 'en' ? 'en-GB' : 'ar-SA-u-ca-gregory';

  const defaultOptions = {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  return new Date(dateString).toLocaleDateString(locale, defaultOptions);
};

/**
 * Format date and time with Gregorian calendar
 * @category instant — business timezone applies
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Locale language: 'ar' (default) or 'en'
 * @returns {string} Formatted date and time string
 */
/**
 * Whole minutes between two INSTANTS.
 *
 * @category instant — a duration is instant-minus-instant and carries no timezone at all. (Bucketing
 * durations into a day or a week IS a business-date question and belongs on the server, via
 * `businessCalendar` — see TEMPORAL_FIELD_CATALOGUE.)
 *
 * @returns {number|null} null when either end is missing or unreadable.
 */
export const minutesBetween = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 60000));
};

export const formatDateTime = (dateString, language = 'ar') => {
  if (!dateString) return language === 'en' ? 'Not set' : 'غير محدد';

  const locale = language === 'en' ? 'en-GB' : 'ar-SA-u-ca-gregory';

  return new Date(dateString).toLocaleDateString(locale, {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format relative timestamp — shows time only if today, or short date + time if older
 * @category instant — business timezone applies
 * ⚠️ Two things must stay in step here: the rendered value AND the "is today" test.
 * `Date.toDateString()` answers in the browser's zone and no Intl `timeZone` option
 * can reach it, so it was replaced with `businessDayKey`. Revert one without the
 * other and a 00:30-Riyadh timestamp reads as "today" to a London viewer still on
 * the previous day.
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted string e.g. "14:30" (today) or "15 يناير, 14:30" (older)
 */
export const formatRelativeTime = (dateString, language = 'ar') => {
  if (!dateString) return '';
  const locale = language === 'en' ? 'en-GB' : 'ar-SA-u-ca-gregory';
  const then = new Date(dateString);
  const now = new Date();
  // Compared in the business timezone, so the rendered value and the "today"
  // decision always agree — see businessDayKey above.
  const isToday = businessDayKey(then) === businessDayKey(now);

  if (isToday) {
    return then.toLocaleTimeString(locale, {
      timeZone: BUSINESS_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return then.toLocaleDateString(locale, {
    timeZone: BUSINESS_TIMEZONE,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date and time with short month (no year) — for timestamps like completion dates
 * @category instant — business timezone applies
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Locale language: 'ar' (default) or 'en'
 * @returns {string} Formatted string e.g. "15 يناير, 14:30"
 */
export const formatShortDateTime = (dateString, language = 'ar') => {
  if (!dateString) return language === 'en' ? 'Not set' : 'غير محدد';

  const locale = language === 'en' ? 'en-GB' : 'ar-SA-u-ca-gregory';

  return new Date(dateString).toLocaleDateString(locale, {
    timeZone: BUSINESS_TIMEZONE,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format time-only string (HH:MM) to Arabic — e.g. "16:00" → "٤:٠٠ م"
 * @category wall-clock — NEVER timezone-convert. The Date is built from parts, so
 * a `timeZone` option double-converts and shows the wrong hour outside Riyadh.
 * @param {string} timeString - Time in HH:MM format
 * @returns {string} Arabic formatted time
 */
export const formatTime = (timeString) => {
  if (!timeString) return 'غير محدد';
  const [h, m] = timeString.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return timeString;
  const date = new Date(2000, 0, 1, h, m);
  return date.toLocaleTimeString('ar-SA-u-ca-gregory', { hour: 'numeric', minute: '2-digit' });
};

/**
 * Clock time of an INSTANT in the business timezone — e.g. a stored 09:00Z → "١٢:٠٠ م".
 * Twin of formatTime for real moments (formatTime is for "HH:MM" wall-clock rule strings).
 * @category instant — business timezone applies
 * @param {string|Date} dateString - Instant to format
 * @returns {string} Arabic clock time
 */
export const formatClockTime = (dateString) => {
  if (!dateString) return 'غير محدد';
  return new Date(dateString).toLocaleTimeString('ar-SA-u-ca-gregory', {
    timeZone: BUSINESS_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * Format date with weekday name in Arabic — e.g. "الاثنين، ٦ أبريل ٢٠٢٦"
 * @category instant | date-only — business timezone applies
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string with weekday
 */
export const formatDateWithDay = (dateString) => {
  if (!dateString) return 'غير محدد';

  return new Date(dateString).toLocaleDateString('ar-SA-u-ca-gregory', {
    timeZone: BUSINESS_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Arabic weekday name from a day-of-week index — e.g. 0 → "الأحد"
 * @category n/a — array lookup, no Date involved
 * @param {number} dayOfWeek - 0 (Sunday) … 6 (Saturday)
 * @returns {string} Arabic weekday name
 */
export const formatWeekdayName = (dayOfWeek) => {
  const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  return days[dayOfWeek] ?? 'غير محدد';
};

/**
 * Format date with short month name in Arabic
 * @category instant | date-only — business timezone applies
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string with short month
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return 'غير محدد';

  return new Date(dateString).toLocaleDateString('ar-SA-u-ca-gregory', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date for compact display (numeric format)
 * @category instant | date-only — business timezone applies
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date in DD/MM/YYYY format
 */
export const formatCompactDate = (dateString) => {
  if (!dateString) return 'غير محدد';

  return new Date(dateString).toLocaleDateString('ar-SA-u-ca-gregory', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/* `todayLocalIso` / `toLocalIso` moved to `utils/dateValueHelper.js` — they
   produce values for inputs and query params, not display text. */

/**
 * Format month name in Arabic from various inputs
 * Handles multiple input formats for maximum flexibility
 * @category mixed — do NOT blanket-convert. The `instanceof Date` branch is a real
 * instant (business timezone applies, and it can name the wrong month at a month
 * boundary); the month-number and 'YYYY-MM' branches build a Date from parts and
 * must never be timezone-converted.
 * @param {string|number|Date} input - Month value (1-12, 'Jan'/'January', or Date object)
 * @param {string} language - 'ar' (default) or 'en'; only the 'YYYY-MM' branch reads it so far
 * @returns {string} Month name (e.g., 'يناير', 'فبراير')
 */
export const formatMonthName = (input, language = 'ar') => {
  if (!input) return '-';

  // A real instant — the only branch here the business timezone applies to.
  if (input instanceof Date) {
    return input.toLocaleDateString('ar-SA-u-ca-gregory', { timeZone: BUSINESS_TIMEZONE, month: 'long' });
  }

  // If it's a number (1-12), treat as month number
  if (typeof input === 'number' && input >= 1 && input <= 12) {
    const date = new Date(2024, input - 1, 1); // month is 0-indexed in Date constructor
    return date.toLocaleDateString('ar-SA-u-ca-gregory', { month: 'long' });
  }

  // If it's a 'YYYY-MM' month key (backend byMonth format)
  if (typeof input === 'string' && /^\d{4}-\d{2}$/.test(input)) {
    const locale = language === 'en' ? 'en-GB' : 'ar-SA-u-ca-gregory';
    return new Date(`${input}-01T00:00:00`).toLocaleDateString(locale, { month: 'long' });
  }

  // If it's a string like 'Jan', 'January', etc. (from moment().format('MMM'))
  const englishToMonth = {
    'jan': 0, 'january': 0,
    'feb': 1, 'february': 1,
    'mar': 2, 'march': 2,
    'apr': 3, 'april': 3,
    'may': 4,
    'jun': 5, 'june': 5,
    'jul': 6, 'july': 6,
    'aug': 7, 'august': 7,
    'sep': 8, 'september': 8,
    'oct': 9, 'october': 9,
    'nov': 10, 'november': 10,
    'dec': 11, 'december': 11
  };

  const monthIndex = englishToMonth[input.toLowerCase()];
  if (monthIndex !== undefined) {
    const date = new Date(2024, monthIndex, 1);
    return date.toLocaleDateString('ar-SA-u-ca-gregory', { month: 'long' });
  }

  return input; // Fallback to original value if unrecognized format
};

/**
 * Format a business month key as month + year — "2026-07" → "يوليو 2026".
 *
 * @category date-only — a calendar period with no time-of-day, so it must NOT be
 * timezone-converted. The Date is built from the key's own parts and read back in
 * UTC; using the browser zone would name the previous month west of Greenwich.
 *
 * A named sibling of `formatMonthName` rather than a flag on it: that function
 * answers "which month" (no year) and is used where the year is already on screen
 * (chart axes, `byMonth` labels). Changing its output would silently restyle every
 * one of those call sites.
 *
 * @param {string} monthKey - 'YYYY-MM' business month (backend `periodMonth`)
 * @returns {string} e.g. "يوليو 2026", or '-' if the input is not a month key
 */
export const formatBusinessMonth = (monthKey) => {
  if (typeof monthKey !== 'string' || !/^\d{4}-\d{2}$/.test(monthKey)) return '-';

  const [year, month] = monthKey.split('-').map(Number);
  if (month < 1 || month > 12) return '-';

  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('ar-SA-u-ca-gregory', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric'
  });
};

/*
 * ── Western-digit siblings, for DOCUMENTS ────────────────────────────────────
 *
 * `ar-SA` renders numbers in Arabic-Indic digits (٢٠٢٦). Every money and count
 * figure in the product is Western (`currencyFormatter`, the /finance dashboard,
 * the backend's own Arabic commentary), so a screen mixing the two is a known,
 * accepted inconsistency — flagged in tasks/owner-monthly-report.md §16.3.
 *
 * On paper it stops being acceptable: the owner report prints "3 عقارات" next to
 * "٣٠ يوليو ٢٠٢٦" inside one frozen document. These two functions add the `-nu-latn`
 * numbering-system extension so a document can use ONE numeral system throughout.
 *
 * Named siblings, not a flag on the originals — the module's standing rule. Every
 * existing call site keeps the digits it renders today; only the print document
 * opts in. Resolving the product-wide inconsistency is a separate decision.
 */

/** Latin-numeral locale. The `-nu-latn` extension changes digits only — month
 *  names, word order and RTL behaviour are untouched. */
const AR_LATN_NUMERALS = 'ar-SA-u-ca-gregory-nu-latn';

/**
 * `formatDate` with Western digits — "30 يوليو 2026".
 * @category instant | date-only — business timezone applies, exactly as formatDate
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatDateLatn = (dateString) => {
  if (!dateString) return 'غير محدد';

  return new Date(dateString).toLocaleDateString(AR_LATN_NUMERALS, {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Day of the month alone, with Western digits — "2026-09-16" → "16". For dense calendar headers
 * where the weekday already sits above the number.
 * @category instant | date-only — business timezone applies, exactly as formatDate
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatDayOfMonthLatn = (dateString) => {
  if (!dateString) return '';

  return new Date(dateString).toLocaleDateString(AR_LATN_NUMERALS, {
    timeZone: BUSINESS_TIMEZONE,
    day: 'numeric'
  });
};

/**
 * `formatBusinessMonth` with Western digits — "2026-06" → "يونيو 2026".
 * @category date-only — a calendar period; read back in UTC, never shifted
 * @param {string} monthKey - 'YYYY-MM'
 * @returns {string}
 */
export const formatBusinessMonthLatn = (monthKey) => {
  if (typeof monthKey !== 'string' || !/^\d{4}-\d{2}$/.test(monthKey)) return '-';

  const [year, month] = monthKey.split('-').map(Number);
  if (month < 1 || month > 12) return '-';

  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(AR_LATN_NUMERALS, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric'
  });
};
